import React from 'react'
import loadable from '@loadable/component'
import PropTypes from 'prop-types'
import { graphql } from 'react-apollo'
import loadPage from './loadPage.gql'
import NotFound from '../NotFound'
import Loading from '../../components/pages/Loading'
import BlogPage from '../BlogPage'
import { connect } from 'react-redux'
import { venture, enableContentful as enableContentfulFromConfig } from '../../config'
import { bindActionCreators } from 'redux'
import {
  resetGamesLegalLines,
  addGameLegalLine,
  closeStateModal
} from '../../store/actions'
import { useLocation } from 'react-router'
import useDataTracking from './hooks/useDataTracking'
import useGeoRedirect from './hooks/useGeoRedirect'
import { ApolloError } from 'apollo-client'

const RedirectModal = loadable(() =>
  import(
    /* webpackChunkName: "RedirectModal" */ '@albatross/common-components/dist/components/RedirectModal'
  )
)

const StatePickerModal = loadable(() =>
  import(
    /* webpackChunkName: "StatePickerModal" */ '@albatross/common-components/dist/components/StatePickerModal'
  )
)

const LandingPage = loadable(() =>
  import(
    /* webpackChunkName: "LandingPage" */ '../../components/pages/LandingPage'
  )
)
const GamePage = loadable(() =>
  import(/* webpackChunkName: "PostPage" */ '../../components/pages/GamePage')
)
const PromoPage = loadable(() =>
  import(/* webpackChunkName: "PromoPage" */ '../../components/pages/PromoPage')
)

const LandingOrPostPage = (props) => {
  const {
    data,
    hasMargin,
    resetGamesLegalLines,
    addGameLegalLine,
    isModalOpen,
    closeStateModal
  } = props

  const {
    pageOrGame,
    loading,
    globalComponents,
    globalContent,
    error,
    contentfulHeader,
    contentfulGlobalSettings,
    variables: { enableContentful }
  } = data

  const { appLinks } = globalContent || {}
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)

  useDataTracking(location.pathname)
  const { showRedirectModal, closeRedirectModal, redirectConfig } = useGeoRedirect(queryParams)

  if (loading && !error) {
    return <Loading />
  }
  if (!loading && error && !enableContentful) {
    return error
  }

  const renderContentfulComponent = () => {
    const { contentfulBlog, contentfulPageOrGame, contentfulGame } = data
    let contentfulGlobalComponents = {}
    let contentfulGlobalContent = { ...contentfulGlobalSettings }
    if (contentfulHeader) {
      contentfulGlobalComponents = {
        components: {
          DynamicHeader: {
            globalConfig: {
              ...contentfulHeader.header,
              ...(contentfulGlobalSettings?.searchIcon
                ? { searchIcon: contentfulGlobalSettings.searchIcon }
                : {})
            }
          }
        }
      }
      contentfulGlobalContent = {
        menuItems: contentfulHeader.menuItems,
        dropdownMenu: contentfulHeader.dropdownMenu,
        ...contentfulGlobalContent
      }
    }

    const commonProps = {
      path: location.pathname,
      globalContent: contentfulGlobalContent,
      globalComponents: contentfulGlobalComponents,
      enableContentful
    }

    if (error && error instanceof ApolloError) {
      if (error.graphQLErrors.some((e) => e.extensions?.code === 404)) {
        return (
          <NotFound
            {...props}
            {...commonProps}
          />
        )
      }
    }

    if (contentfulBlog) {
      const shouldShowBlogPage = location.pathname.split('/').includes(contentfulBlog.subCategoryId)

      if (shouldShowBlogPage) {
        return (
          <BlogPage
            {...props}
            {...commonProps}
            game={contentfulBlog}
         />
        )
      }

      return (
        <NotFound
          {...props}
          {...commonProps}
        />
      )
    }

    if (contentfulPageOrGame || contentfulGame) {
      const gameOverride = contentfulPageOrGame?.game ?? contentfulGame
      if (gameOverride) {
        if (gameOverride?.link || gameOverride?.customRegistrationUrl) {
          const headerButtons = commonProps.globalComponents.components.DynamicHeader.globalConfig?.buttons
          commonProps.globalComponents.components.DynamicHeader.globalConfig.buttons = {
            ...headerButtons,
            button2: {
              ...headerButtons.button2,
              href: gameOverride?.link || gameOverride?.customRegistrationUrl
            }
          }
        }
        if (gameOverride?.introduction === '' && gameOverride?.howToPlay) {
          const [intro, ...content] = gameOverride.howToPlay.split('<hr/>')
          if (content.length) {
            gameOverride.introduction = intro.trim()
            gameOverride.howToPlay = content.join('').trim()
          }
        }
        return (
          <GamePage
            {...props}
            {...commonProps}
            game={gameOverride}
          />
        )
      }
      const page = contentfulPageOrGame?.page
      if (page) {
        const pageMetaTags = [...page.metaTags, ...contentfulGlobalSettings.metaTags]
        if (page?.overrideComponents?.header?.override) {
          commonProps.globalComponents.components.DynamicHeader.globalConfig = page?.overrideComponents?.header
          commonProps.globalContent.menuItems = page?.overrideComponents?.menuItems
          commonProps.globalContent.dropdownMenu = page?.overrideComponents?.dropdownMenu
        }

        return (
          <LandingPage
            {...props}
            {...page}
            {...commonProps}
            path={location.pathname}
            hasMargin={hasMargin}
            metaTags={pageMetaTags}
          />
        )
      }
    }
  }

  const renderLegacyComponent = () => {
    const { game, page } = pageOrGame || {}

    const commonProps = {
      globalContent,
      globalComponents,
      enableContentful
    }

    const shouldShowBlogPage =
      game?.categoryId === 'blog' &&
      game?.visible &&
      location.pathname.includes(game.subCategoryId)

    if (shouldShowBlogPage) {
      return (
        <BlogPage
          game={game}
          globalContent={globalContent}
          globalComponents={globalComponents}
        />
      )
    }

    if (game?.visible && game?.categoryId !== 'blog') {
      return (
        <GamePage
          {...props}
          {...commonProps}
          game={game}
          resetGamesLegalLines={resetGamesLegalLines}
          addGameLegalLine={addGameLegalLine}
        />
      )
    }

    if (page?.published) {
      const isPromo = location.pathname.includes('/promo/')
      const PageComponent = isPromo ? PromoPage : LandingPage

      return (
        <PageComponent
          {...props}
          {...page}
          {...commonProps}
          hasMargin={hasMargin}
        />
      )
    }

    return (
      <NotFound
        {...props}
        {...commonProps}
      />
    )
  }

  const Component = enableContentful
    ? renderContentfulComponent()
    : renderLegacyComponent()

  return (
    <>
      {Component}
      {isModalOpen && venture === 'ballycasinonj' && (
        <StatePickerModal
          venture={venture}
          {...globalComponents.components.StateModal}
          {...appLinks}
          onClose={closeStateModal}
        />
      )}
        <RedirectModal
          show={showRedirectModal}
          venture={venture}
          closeMessage={'Stay here'}
          submitMessage={redirectConfig?.redirectButton}
          promptMessage={'CHOOSE YOUR LOCATION'}
          promptDescription={redirectConfig?.redirectPrompt}
          onClose={closeRedirectModal}
          onSubmit={() => window.location.replace(redirectConfig?.redirectTo)}
        />
    </>
  )
}

LandingOrPostPage.propTypes = {
  data: PropTypes.object,
  resetGamesLegalLines: PropTypes.func,
  addGameLegalLine: PropTypes.func,
  closeStateModal: PropTypes.func,
  hasMargin: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  location: PropTypes.object,
  ConfigContxt: PropTypes.object
}

function mapStateToProps (state) {
  return {
    isModalOpen: state.updateStateModal.open
  }
}

function mapDispatchToProps (dispatch) {
  return bindActionCreators(
    { resetGamesLegalLines, addGameLegalLine, closeStateModal },
    dispatch
  )
}

const LandingPageContainerWithData = graphql(loadPage, {
  options: (props) => {
    const { location, match } = props
    const { pathname, search, state } = location
    const { params } = match

    const isBlog = 'categoryId' in params && 'blogId' in params
    const isGame = state?.isGame ?? false

    const urlParams = new URLSearchParams(search)

    const enableContentfulOverride = urlParams.get('enableContentful') === 'true'

    const preview = urlParams.get('preview') === 'true'

    const enableContentful = preview || (urlParams.get('enableContentful') !== null ? enableContentfulOverride : enableContentfulFromConfig)

    const variant = props.variant ? parseInt(props.variant, 10) : null

    if (enableContentful) {
      return {
        variables: {
          path: pathname !== '/' ? pathname.slice(1) : 'home',
          categoryId: params?.categoryId,
          released: true,
          enableContentful,
          resourceId: (params?.blogId || params?.gameId) ?? '',
          isPage: !(isBlog || isGame),
          isBlog,
          isGame,
          variant,
          preview
        }
      }
    }
    return {
      variables: {
        path: pathname,
        released: true,
        enableContentful,
        resourceId: null,
        isPage: false,
        isBlog: false,
        isGame: false,
        variant,
        preview: false
      }
    }
  }
})(LandingOrPostPage)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LandingPageContainerWithData)
